import React, { useState, useEffect } from 'react';
import { User, X } from 'lucide-react';
import { db } from '../../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import { createStudentViewPath } from '../../utils/studentRoutes';

const MentorOverviewWidget = () => {
  const [mentors, setMentors] = useState([]);
  const [batchData, setBatchData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [menteeList, setMenteeList] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch all staff
      const usersSnap = await getDocs(collection(db, 'users'));
      const allStaff = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const mentorsList = allStaff.filter(s => s.role === 'mentor');
      
      // Fetch batch assignments
      const batchSnap = await getDocs(collection(db, 'batchAssignments'));
      const batches = {};
      
      batchSnap.docs.forEach(doc => {
        const batch = doc.data();
        if (batch.mentorEmail) {
          if (!batches[batch.mentorEmail]) {
            batches[batch.mentorEmail] = [];
          }
          batches[batch.mentorEmail].push({
            batch: batch.batch,
            division: batch.division,
            count: 0
          });
        }
      });

      // Fetch students and count per batch
      const studentsSnap = await getDocs(collection(db, 'students'));
      
      studentsSnap.docs.forEach(doc => {
        const student = doc.data();
        const mentorEmail = student.mentorEmail;
        const studentBatch = student.batch;
        
        if (mentorEmail && batches[mentorEmail]) {
          batches[mentorEmail].forEach(b => {
            if (b.batch === studentBatch) {
              b.count++;
            }
          });
        }
      });

      setMentors(mentorsList);
      setBatchData(batches);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleBatchClick = async (batch) => {
    try {
      const studentsSnap = await getDocs(collection(db, 'students'));
      const students = studentsSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(s => s.batch === batch);
      
      setMenteeList(students);
      setSelectedBatch(batch);
    } catch (error) {
      console.error('Error loading mentees:', error);
    }
  };

  return (
    <>
      <div className="space-y-3 max-h-[250px] overflow-y-auto pr-2">
        {loading ? (
          <p className="text-center py-8 text-gray-500">Loading...</p>
        ) : mentors.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No mentors assigned</p>
        ) : (
          mentors.map((mentor, idx) => {
            const mentorBatches = batchData[mentor.email] || [];
            const totalMentees = mentorBatches.reduce((sum, b) => sum + b.count, 0);
            
            return (
              <div key={idx} className="p-3 border rounded-lg hover:bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold">{mentor.name}</p>
                      <p className="text-sm text-gray-600">{mentor.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-purple-600">{totalMentees}</p>
                    <p className="text-xs text-gray-500">mentees</p>
                  </div>
                </div>
                
                {mentorBatches.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {mentorBatches.map((batchInfo, i) => (
                      <button
                        key={i}
                        onClick={() => handleBatchClick(batchInfo.batch)}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors cursor-pointer"
                      >
                        {batchInfo.batch} ({batchInfo.count})
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Mentee List Modal */}
      {selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Batch {selectedBatch} - Mentees ({menteeList.length})
              </h3>
              <button 
                onClick={() => setSelectedBatch(null)} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              {menteeList.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No mentees found</p>
              ) : (
                menteeList.map((student) => (
                  <div key={student.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-semibold">
                          {(student.firstName || student.studentName || 'S').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1">
                        <button
                          type="button"
                          onClick={() => window.open(createStudentViewPath('/student/mahiti', student.rollNumber), '_blank', 'noopener,noreferrer')}
                          className="font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                        >
                          {student.firstName && student.surname 
                            ? `${student.firstName} ${student.surname}` 
                            : student.studentName || 'Unknown'}
                        </button>
                        <p className="text-sm text-gray-600">{student.rollNumber || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">{student.email || 'N/A'}</p>
                      <p className="text-sm text-gray-500">{student.mobile || 'N/A'}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MentorOverviewWidget;
